/**
 * Attribution tracking utilities
 * Captures and persists marketing attribution data (UTM params, fbclid, etc.)
 */

export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  fbp?: string;
  fbc?: string;
  firstUrl?: string;
  referrer?: string;
  capturedAt?: string;
}

const ATTRIBUTION_KEY = "wealthbee_attribution";
const ATTRIBUTION_CAPTURED_KEY = "wealthbee_attribution_captured";

/**
 * Capture attribution data from URL parameters and browser context
 * Should be called on first page load
 */
export const captureAttribution = (): AttributionData => {
  if (typeof window === "undefined") {
    return {};
  }

  // Check if we've already captured attribution
  const alreadyCaptured = localStorage.getItem(ATTRIBUTION_CAPTURED_KEY);
  if (alreadyCaptured) {
    return getAttribution();
  }

  const urlParams = new URLSearchParams(window.location.search);

  const attribution: AttributionData = {
    utm_source: urlParams.get("utm_source") || undefined,
    utm_medium: urlParams.get("utm_medium") || undefined,
    utm_campaign: urlParams.get("utm_campaign") || undefined,
    utm_term: urlParams.get("utm_term") || undefined,
    utm_content: urlParams.get("utm_content") || undefined,
    fbclid: urlParams.get("fbclid") || undefined,
    firstUrl: window.location.href,
    referrer: document.referrer || undefined,
    capturedAt: new Date().toISOString(),
  };

  // Get Facebook cookies (these are set by Meta Pixel)
  attribution.fbp = getFbpCookie();
  attribution.fbc = getFbcCookie(attribution.fbclid);

  // Only save if we have at least some attribution data
  const hasData = Object.values(attribution).some(
    (value) => value !== undefined && value !== ""
  );

  if (hasData) {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    localStorage.setItem(ATTRIBUTION_CAPTURED_KEY, "true");
  }

  return attribution;
};

/**
 * Get stored attribution data
 */
export const getAttribution = (): AttributionData => {
  if (typeof window === "undefined") {
    return {};
  }

  const stored = localStorage.getItem(ATTRIBUTION_KEY);
  if (!stored) {
    return {};
  }

  try {
    return JSON.parse(stored);
  } catch (e) {
    console.error("Error parsing attribution data:", e);
    return {};
  }
};

/**
 * Update attribution data (e.g., when fbp/fbc cookies become available)
 */
export const updateAttribution = (data: Partial<AttributionData>): void => {
  if (typeof window === "undefined") {
    return;
  }

  const current = getAttribution();
  const updated = { ...current, ...data };

  localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(updated));
};

/**
 * Clear attribution data (e.g., on logout or after successful conversion)
 */
export const clearAttribution = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(ATTRIBUTION_KEY);
  localStorage.removeItem(ATTRIBUTION_CAPTURED_KEY);
};

/**
 * Get the Facebook Browser ID (_fbp cookie)
 */
export const getFbpCookie = (): string | null => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|;)\s*_fbp=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Get or generate the Facebook Click ID (_fbc cookie)
 */
export const getFbcCookie = (fbclid?: string): string | null => {
  if (typeof document === "undefined") return null;

  // First check if _fbc cookie already exists
  const match = document.cookie.match(/(?:^|;)\s*_fbc=([^;]+)/);
  if (match) {
    return decodeURIComponent(match[1]);
  }

  // If no _fbc cookie but we have fbclid, generate it
  if (fbclid) {
    // Format: fb.1.timestamp.fbclid
    const timestamp = Date.now();
    const fbc = `fb.1.${timestamp}.${fbclid}`;

    // Set the cookie (180 days expiry)
    const expires = new Date();
    expires.setDate(expires.getDate() + 180);
    document.cookie = `_fbc=${fbc}; expires=${expires.toUTCString()}; path=/; domain=${getDomain()}`;

    return fbc;
  }

  return null;
};

/**
 * Get the domain for cookie setting
 */
const getDomain = (): string => {
  if (typeof window === "undefined") return "";

  const hostname = window.location.hostname;

  // For localhost, don't set domain
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "";
  }

  // For production, use the base domain
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }

  return hostname;
};

/**
 * Generate a unique event ID for deduplication between Pixel and Conversions API
 */
export const generateEventId = (): string => {
  // Generate a UUID v4
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Check if user came from a Facebook ad
 */
export const isFromFacebookAd = (): boolean => {
  const attribution = getAttribution();
  return !!(
    attribution.fbclid ||
    attribution.fbc ||
    attribution.utm_source === "facebook" ||
    attribution.utm_source === "fb" ||
    attribution.utm_source === "instagram" ||
    attribution.utm_source === "ig"
  );
};

export default {
  capture: captureAttribution,
  get: getAttribution,
  update: updateAttribution,
  clear: clearAttribution,
  generateEventId,
  isFromFacebookAd,
  getFbp: getFbpCookie,
  getFbc: getFbcCookie,
};
